import { Box, NumberField, SelectField, Text } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Sparkles } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import PriceInfo from 'modules/upsell/components/SelfUpsell/CheckoutModal/PriceInfo'
import type PlanVariant from 'modules/upsell/lib/selfUpsell/plans/PlanVariant'
import { PeriodUnit, UpsellDiscount } from 'modules/upsell/lib/selfUpsell/types'

type BusinessPlanSelectorProps = {
  options: Array<{ label: string; value: string; disabled?: boolean }>
  selectedValue: string
  onPlanChange: (selectedId: string) => void
}

export const OneSelector = ({
  options,
  selectedValue,
  onPlanChange,
}: BusinessPlanSelectorProps) => {
  return (
    <Box paddingBottom="s8" gap="s6">
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('growth.self_upsell.one_selector')}
      </Text.Normal>
      <SelectField
        hideLabel
        options={options}
        value={selectedValue}
        onChange={(value) => onPlanChange(value)}
        selectValue={selectedValue}
        missingLabel={i18n.t('growth.self_upsell.one_selector')}
      />
    </Box>
  )
}

export const RecruitmentSelector = ({
  options,
  selectedValue,
  onPlanChange,
}: BusinessPlanSelectorProps) => {
  return (
    <Box paddingBottom="s8" gap="s6">
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('self_upsell.first_step.choose_active_job_openings')}
      </Text.Normal>
      <SelectField
        hideLabel
        options={options}
        value={selectedValue}
        onChange={(value) => onPlanChange(value)}
        selectValue={selectedValue}
        missingLabel={i18n.t('self_upsell.first_step.choose_active_job_openings')}
      />
    </Box>
  )
}

type ExpensesSeatsSelectorProps = {
  seats: number
  currentSeats: number
  includedSeatsPerModule: number | undefined
  onSeatsChange: (value: number) => void
}

export const ExpensesSeatsSelector = ({
  seats,
  currentSeats,
  includedSeatsPerModule,
  onSeatsChange,
}: ExpensesSeatsSelectorProps) => {
  return (
    <Box paddingBottom="s8" gap="s6">
      <Text.Normal size="100" color="grey700" weight="medium">
        {i18n.t('self_upsell.first_step.expenses_seat_label')}
      </Text.Normal>
      <NumberField
        locale="en-US"
        onChange={(value) => {
          onSeatsChange(value || 0)
        }}
        value={seats}
        min={includedSeatsPerModule || 1}
        max={currentSeats}
        step={1}
        error={
          seats > currentSeats
            ? i18n.t('self_upsell.first_step.expenses_seat_max_error', {
                maxSeats: currentSeats,
              })
            : includedSeatsPerModule && seats < includedSeatsPerModule
              ? i18n.t('self_upsell.first_step.expenses_seat_min_error', {
                  minSeats: includedSeatsPerModule,
                })
              : undefined
        }
        missingLabel={i18n.t('self_upsell.first_step.expenses_seat_label', {
          includedSeatsPerModule,
          currentSeats,
        })}
        selectValue={seats.toString()}
      />
    </Box>
  )
}

type VariantOptionCardProps = {
  title: string
  isSelected: boolean
  showPrice: boolean
  showBestValueTag?: boolean
  variant: PlanVariant | undefined
  period: PeriodUnit
  currency: string
  seats: number
  currentSeats: number
  discount: UpsellDiscount | undefined
  onClick: () => void
}

export const VariantOptionCard = ({
  title,
  isSelected,
  showPrice,
  showBestValueTag = false,
  variant,
  period,
  currency,
  seats,
  currentSeats,
  discount,
  onClick,
}: VariantOptionCardProps) => {
  return (
    <Box
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: isSelected ? 'viridian1000' : 'grey400',
        },
      }}
      borderRadius={{ all: 'abs016' }}
      background={isSelected ? 'viridian100' : 'grey000White'}
      padding="s16"
      gap="s8"
      justifyContent="spaceBetween"
      onClick={onClick}
      style={{
        height: '88px',
      }}
    >
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
        <Text.Normal size="200" weight="semibold" color="grey1000">
          {title}
        </Text.Normal>
        {showBestValueTag && <F0TagRaw text="Best value" icon={Sparkles} />}
      </Box>
      {showPrice && (
        <PriceInfo
          variant={variant}
          period={period}
          currency={currency}
          seats={seats}
          currentSeats={currentSeats}
          discount={discount}
        />
      )}
    </Box>
  )
}
